import { useSaveBillablePrice } from '@domains/subscription/priv/api/queries'
import { useCreateBillableTemplatePrice } from '@domains/subscription/priv/Billables/api/query'
import type { BillableFormPluginProps, PriceRowPlugin } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { CreatePricePlugin, CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import { TieredPriceForm } from '@domains/subscription/priv/Billables/usecases/Price/tiered/TieredPriceForm'
import { TieredPriceRow } from '@domains/subscription/priv/Billables/usecases/Price/tiered/TieredPriceRow'
import { isTiered } from '@domains/subscription/priv/Billables/usecases/Price/tiered/tieredMapper'
import type { BaseAlgorithm, BasePrice } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type { CreatePriceRequest } from '@domains/subscription/priv/types'
import { type ComponentType, memo } from 'react'

const TieredPriceFormFactory = (props: CreatePricePluginProps) => {
  const { mutate } = useCreateBillableTemplatePrice(props.billableId, props.templateId)

  const handleCreatePrice = (price: BasePrice) => {
    if (!isTiered(price)) {
      throw new Error(`Attempted to create a non-tiered price with TieredPriceFormFactory: ${price.type}`)
    }

    mutate(price, {
      onSuccess: () => {
        props.onCreatedPrice(price)
      },
    })
  }

  const getAlgorithmPlugin = (algorithm: BaseAlgorithm) => {
    const plugin = props.additionalPlugins?.find((plugin) => plugin.supports(algorithm.type))

    return plugin?.FormComponent
  }

  return <TieredPriceForm {...props} mode="nested" getAlgorithmPlugin={getAlgorithmPlugin} onCreatedPrice={handleCreatePrice} />
}

export class TieredCreatePricePlugin implements CreatePricePlugin {
  constructor(public additionalPlugins?: CreatePricePlugin[]) {}
  type = 'Tiered'
  supports(type): boolean {
    return type === 'Tiered'
  }
  Component: ComponentType<CreatePricePluginProps> = TieredPriceFormFactory
}

const TieredPriceRowFactory = memo((props: BillableFormPluginProps) => {
  const { mutateAsync: saveBillable } = useSaveBillablePrice(props.templateId)

  const handleSave = async (data: CreatePriceRequest) => {
    await saveBillable({
      billableId: data.billableId,
      billablePrice: data,
    })
  }

  if (!isTiered(props.price)) {
    return null
  }

  return <TieredPriceRow {...props} additionalPlugins={props.additionalPlugins} tieredPrice={props.price} onSubmit={handleSave} />
})

export class TieredPriceRowPlugin implements PriceRowPlugin {
  constructor(public additionalPlugins: PriceRowPlugin[]) {}
  type = 'Tiered'

  supports(algorithm): boolean {
    return algorithm.type === this.type
  }

  Component: ComponentType<BillableFormPluginProps> = TieredPriceRowFactory
}
